import { useEffect, useRef, useState } from "react";
import { evaluateModifierStackAsync } from "../modifierApply.ts";
import type {
  ModifierGeometry,
  ModifierWorkerRequest,
} from "./modifierTypes.ts";

/**
 * Latest-only authoritative modifier evaluation for React renderers.
 *
 * At most one worker request is in flight per hook instance. While a request
 * is running, subsequent updates collapse into a single pending request so
 * slider drags do not enqueue an unbounded backlog. When a new request arrives
 * while another authoritative result is already available, the previous result
 * stays live until the replacement result lands so the viewport does not flash.
 */
export function useModifierEvaluation(
  request: Omit<ModifierWorkerRequest, "cacheEpoch"> | null,
): ModifierGeometry | null {
  const [resolved, setResolved] = useState<ModifierGeometry | null>(null);
  const unmountedRef = useRef(false);
  const runningRef = useRef(false);
  const pendingRef = useRef<{
    seq: number;
    request: Omit<ModifierWorkerRequest, "cacheEpoch">;
  } | null>(null);
  const latestSeqRef = useRef(0);
  const pumpRef = useRef<() => void>(() => {});

  useEffect(
    () => () => {
      unmountedRef.current = true;
    },
    [],
  );

  pumpRef.current = () => {
    if (runningRef.current || unmountedRef.current) return;
    runningRef.current = true;

    void (async () => {
      try {
        while (pendingRef.current && !unmountedRef.current) {
          const next = pendingRef.current;
          pendingRef.current = null;
          try {
            const geometry = await evaluateModifierStackAsync(next.request);
            if (!unmountedRef.current && next.seq === latestSeqRef.current) {
              setResolved(geometry);
            }
          } catch (error) {
            if (!unmountedRef.current && next.seq === latestSeqRef.current) {
              console.warn("Modifier evaluation failed:", error);
            }
          }
        }
      } finally {
        runningRef.current = false;
        if (pendingRef.current && !unmountedRef.current) {
          pumpRef.current();
        }
      }
    })();
  };

  useEffect(() => {
    latestSeqRef.current++;
    if (!request) {
      pendingRef.current = null;
      setResolved(null);
      return;
    }

    pendingRef.current = {
      seq: latestSeqRef.current,
      request,
    };
    pumpRef.current();
  }, [request]);

  return resolved;
}
